import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian12() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "12");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="XWGeBoR6dE4"
        title="Hey Unnai Thaane — Ilaiyaraaja · Kadhal Parisu (1987)"
        caption="Listen for chords that sound neither clearly major nor minor. That open quality is quartal harmony — chords built from stacked 4ths instead of 3rds."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/12-hey-unnai-thaane-page1.png"
        caption="Jason's chart — the song's scale, D natural minor (D E F G A B♭ C), then 'Quartal Chords (intervals of 4ths)': a stack of 4ths over each bass note — G C F over D, A D G over E, B♭ E A over F, C F B♭ over G, D G C over A, E A D over B♭, F B♭ E over C — with the three stacks that contain the B♭–E tritone marked ♯4."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/12-hey-unnai-thaane-page2.png"
        caption="Page 2 — the progression Dm7 Dm7 Gm7 Dm7, marked 'quartal', and Fmaj7 Fmaj7 B♭maj7 Fmaj7 (written as A C E over F and A D F over B♭), plus the drum pattern on a 1-e-&-a grid: kick, clap, hi-hats, high and low conga."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/12-hey-unnai-thaane-page3.png"
        caption="Page 3 — the riff in octaves (D F G A, then A C D) over Dm7, B♭maj7, Am7 and Dm7, and a pentatonic-minor line harmonised in 5ths."
      />

      <SISCard label="The big idea" title="Quartal harmony — the sound with no gender">
        <p>
          Every chord you've learned so far is built by <strong>stacking thirds</strong>: C–E–G (two 3rds stacked). This produces triads with a clear identity: major (bright) or minor (dark). The quality of a chord — its emotional "gender" — is determined by those stacked 3rds.
        </p>
        <p>
          Jason: <em>"Usually when we build chords it's in intervals of thirds, no? Now here our composer is doing it in fourths."</em> Quartal harmony swaps the 3rd for a <strong>4th</strong> as the building interval. Stack two 4ths: C–F–B♭. That's a quartal triad. It has no major 3rd, no minor 3rd — no gender. It sounds <strong>open, suspended, floating</strong>. Neither happy nor sad. Neither ancient nor modern. It simply <em>is</em>.
        </p>
        <p>
          <em>Hey Unnai Thaane</em> comes from <em>Kadhal Parisu</em> (1987), sung by S. P. Balasubrahmanyam and S. Janaki. Jason builds his quartal chords from the song's scale, D natural minor: D E F G A B♭ C. He hears the intro as <em>"very disco, disco influenced from the 80s"</em> — seventh chords, quartal chords and a funky groove in well under a minute of music.
        </p>
      </SISCard>

      <SISCard label="Interval anatomy" title="Why 4ths build this particular sound">
        <p>
          Compare these two chords, both starting on C:
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-amber-700/20 text-left text-[11px] uppercase tracking-wider text-indigo-800">
                <th className="pb-2 pr-4">Chord type</th>
                <th className="pb-2 pr-4">Notes</th>
                <th className="pb-2 pr-4">Intervals stacked</th>
                <th className="pb-2">Quality</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-amber-700/10">
                <td className="py-2 pr-4 font-semibold">C major triad</td>
                <td className="py-2 pr-4">C–E–G</td>
                <td className="py-2 pr-4">Major 3rd + Minor 3rd</td>
                <td className="py-2">Bright, happy, decisive</td>
              </tr>
              <tr className="border-b border-amber-700/10">
                <td className="py-2 pr-4 font-semibold">C minor triad</td>
                <td className="py-2 pr-4">C–E♭–G</td>
                <td className="py-2 pr-4">Minor 3rd + Major 3rd</td>
                <td className="py-2">Dark, sad, emotional</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-semibold">C quartal triad</td>
                <td className="py-2 pr-4">C–F–B♭</td>
                <td className="py-2 pr-4">Perfect 4th + Perfect 4th</td>
                <td className="py-2">Open, suspended, genderless</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3">
          The key insight: <strong>the 3rd is the 'gender' note</strong>. Major 3rd = major chord. Minor 3rd = minor chord. Remove the 3rd entirely (as quartal harmony does) and the chord loses its gender. This can sound ambiguous or mysterious — or, in the right context, it can sound fresh and modern.
        </p>
        <p>
          One warning: if one of the stacked 4ths is a <strong>tritone</strong> (augmented 4th — like C to F♯), the quartal chord gains sharp tension. Pure quartal chords use only <em>perfect</em> 4ths. When you hit a tritone, the chord "sharpens."
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Quartal chords in D minor — from Jason's chart"
        caption="Each chord stacks notes a 4th apart over a bass note, using only notes of D natural minor, and the shape moves up the scale. Most of the 4ths are perfect; wherever B♭ meets E the 4th is augmented — a tritone — and Jason marks those three stacks ♯4. The numbers above are the scale degree of the bass."
        chords={[
          { chord: "G-C-F/D",  rn: "1",  pitches: ["D3","G3","C4","F4"] },
          { chord: "A-D-G/E",  rn: "2",  pitches: ["E3","A3","D4","G4"] },
          { chord: "B♭-E-A/F", rn: "♭3", function: "Tension", pitches: ["F3","Bb3","E4","A4"] },
          { chord: "C-F-B♭/G", rn: "4",  pitches: ["G3","C4","F4","Bb4"] },
          { bar: true },
          { chord: "D-G-C/A",  rn: "5",  pitches: ["A3","D4","G4","C5"] },
          { chord: "E-A-D/B♭", rn: "♭6", function: "Tension", pitches: ["Bb3","E4","A4","D5"] },
          { chord: "F-B♭-E/C", rn: "♭7", function: "Tension", pitches: ["C4","F4","Bb4","E5"] },
        ]}
      />

      <RomanNumeralAnalysis
        label="The progression — D minor, played quartal"
        caption="Jason's chart writes Dm7 Dm7 Gm7 Dm7 and marks it 'quartal': voice each chord as a stack of 4ths — D–G–C–F and G–C–F–B♭. Jason calls the first one a 'D quartal' chord. Each stack keeps the root, minor 3rd and 7th of the m7 chord but swaps its 5th for the 11th (the 4th) — which is why the boxes are labelled by the stack rather than as plain Dm7 and Gm7. The second line moves to Fmaj7 and B♭maj7, written on the chart as A C E over F and A D F over B♭."
        chords={[
          { chord: "D quartal",      rn: "i (Dm7)",  function: "Tonic",        pitches: ["D3","G3","C4","F4"] },
          { chord: "D quartal",      rn: "i (Dm7)",  function: "Tonic",        pitches: ["D3","G3","C4","F4"] },
          { chord: "G quartal",      rn: "iv (Gm7)", function: "Pre-dominant", pitches: ["G3","C4","F4","Bb4"] },
          { chord: "D quartal",      rn: "i (Dm7)",  function: "Tonic",        pitches: ["D3","G3","C4","F4"] },
          { bar: true },
          { chord: "Fmaj7",          rn: "IIImaj7",  function: "Mediant",      pitches: ["F3","A3","C4","E4"] },
          { chord: "Fmaj7",          rn: "IIImaj7",  function: "Mediant",      pitches: ["F3","A3","C4","E4"] },
          { chord: "B♭maj7",         rn: "VImaj7",   function: "Submediant",   pitches: ["Bb2","A3","D4","F4"] },
          { chord: "Fmaj7",          rn: "IIImaj7",  function: "Mediant",      pitches: ["F3","A3","C4","E4"] },
        ]}
      />

      <SISCard label="Bass guitar genealogy" title="Thinking like a bass player">
        <p>
          Jason's key insight: <em>"By the way, Ilaiyaraaja is a bass guitarist. You should do some historic analysis. So he's thinking like a bass player."</em>
        </p>
        <p>
          A standard 4-string bass guitar is tuned <strong>E–A–D–G</strong>. Those intervals? All perfect 4ths. Every bassist, from the moment they pick up the instrument, has their fingers and muscle memory organised around 4ths. The fret patterns for scales, the shapes for arpeggios, the natural resting positions for the left hand — all of it is structured around the interval of a 4th.
        </p>
        <p>
          That is Jason's point: for someone who thinks like a bass player, stacking 4ths on a keyboard is a natural move rather than an intellectual exercise. (He notes, half-joking, that the guitar's tuning — all 4ths except one major 3rd between the G and B strings — is why people choose the bass guitar.)
        </p>
        <p>
          In jazz, the pianist McCoy Tyner's quartal voicings with John Coltrane's quartet were hugely influential, and one famous voicing even has a name: the "So What" chord — three perfect 4ths with a major 3rd on top — from Bill Evans's piano on Miles Davis's "So What".
        </p>
      </SISCard>

      <SISInfluence
        artists={[
          "Earth, Wind & Fire",
          "Tina Turner",
          "McCoy Tyner — with John Coltrane's quartet (quartal piano voicings)",
          "Miles Davis — 'So What' (Bill Evans's 'So What' chord)",
          "Herbie Hancock — 'Maiden Voyage' (suspended, fourth-based chords)",
        ]}
      >
        <p>
          Jason hears 1980s disco and funk in this intro — he names Earth, Wind & Fire and Tina Turner — along with Brazilian grooves and quartal voicings that come from jazz. For more quartal sounds, listen to McCoy Tyner's piano with John Coltrane's quartet and to the "amen" response in Miles Davis's "So What". Then come back to <em>Hey Unnai Thaane</em> and listen for the same open, neither-major-nor-minor colour in a Tamil film song.
        </p>
      </SISInfluence>

      <SISCallout label="Practice drill — stack 4ths in every key">
        <p>
          Take any note on the piano. Add the note a perfect 4th above it (count 5 semitones). Add another perfect 4th above that (another 5 semitones). You now have a quartal triad. Play all three together. Now do what Jason's chart does in D minor: on each note of the scale, stack 4ths using only the notes D E F G A B♭ C — D–G–C–F, E–A–D–G, F–B♭–E–A, G–C–F–B♭, A–D–G–C, B♭–E–A–D, C–F–B♭–E. Most of those 4ths are perfect (5 semitones); the three stacks where B♭ meets E contain a tritone (6 semitones) and sound sharper. Play the full sequence and feel how the shape creates motion without ever settling on major or minor.
        </p>
      </SISCallout>

      <SISTakeaway>
        <p>
          Quartal harmony is one of the quickest ways to freshen your sound. Take a chord in a progression and re-voice it as a stack of 4ths — as Jason does with Dm7 and Gm7 here — and the music immediately sounds more open. You will hear quartal voicings in jazz, funk, fusion and film scores. The reason they sound fresh is that a stack of 4ths sits outside the major/minor binary. Learn to play in 4ths and you add a third option to your harmonic vocabulary alongside major and minor: <em>neither</em>.
        </p>
      </SISTakeaway>
    </LessonShell>
  );
}
